import React, { useEffect, useContext } from 'react';
import { AppContext } from '@thd-nucleus/app-render';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { Metadata } from '@thd-olt-component-react/metadata';
import { Typography } from '@one-thd/sui-atomic-components';
import {
  getContentfulTargetingIds,
  useVariationIds,
} from '@thd-olt-component-react/personalization-utils';
import { Col, Row } from '@thd-olt-component-react/core-ui';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { useDisabledGiftCardFlow } from '@thd-olt-component-react/gift-cards-information';
import proptypes from 'prop-types';
const { bool, shape, string } = proptypes;
import classNames from 'classnames/bind.js';
import { giftCardDataModel } from '../../ExperienceDataModel';
import { FEATURE_SWITCH_NAMES } from '../../../utils/consts';

import GiftCardLayoutMainSection from './GiftCardLayoutMainSection';
import { componentMapper } from '../../utils/componentMapper';
import {
  getAnalyticsPageType, getContentBreadCrumbLabels, parseContent, renderRobotTag
} from '../../utils/utils';
import styles from '../../templates/giftCard-layout.scss';

export const GiftCardLayout = ({
  data,
  loading,
  path,
  pageId,
  pathWithQueryParams,
  previewLink,
  slug
}) => {
  const cx = classNames.bind(styles);
  const { instance } = useContext(AppContext);
  const { isFlowDisabled } = useDisabledGiftCardFlow(FEATURE_SWITCH_NAMES.DISABLE_GIFT_CARD_PURCHASE);
  const layouts = data?.layouts;
  const content = layouts?.content;
  const crumbs = layouts?.breadcrumbs?.breadcrumbItem || [];
  const metadata = layouts?.seo || {};
  const { linkData, scriptData, ...metadataMinusLinkDataAndScriptData } = metadata;

  const { mboxes, campaignIds } = getContentfulTargetingIds({ content });

  const isReady = mboxes?.length > 0 || campaignIds?.length > 0;

  const { variationIds, renderDefault } = useVariationIds({
    mboxes,
    campaignIds,
    isReady
  });

  const customProps = {
    ComponentSelector: (component) => ({
      componentMap: componentMapper.getComponentMap(),
      defaultComponent: component.defaultComponent,
      variations: component.variations,
      variationIds: variationIds || [],
      renderDefault,
      isSeoBot: instance.thdSEOBotDetection,
      targetingType: component.targetingType,
      customPropMap: {
        Spotlight: {
          showDescription: true
        },
        SpotlightContainer: {
          showDescription: true
        }
      }
    }),
    Spotlight: () => ({
      showDescription: true
    }),
    SpotlightContainer: () => ({
      showDescription: true
    })
  };

  const breadCrumbLabels = getContentBreadCrumbLabels(crumbs);
  const pageType = getAnalyticsPageType(pathWithQueryParams);
  const breadcrumbs = {
    searchModel: {
      taxonomy: {
        breadCrumbs: crumbs
      }
    }
  };

  const { Content } = parseContent({
    data,
    componentMapper,
    customProps
  });

  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('speciality-content.page-load', { breadCrumbLabels, pageType, pageId });
  }, []);

  return (
    <Row name="giftCard-layout-manager">
      <ErrorBoundary name="metadata">
        <Metadata data={{ ...metadataMinusLinkDataAndScriptData }}>
          {renderRobotTag(metadata, previewLink)}
        </Metadata>
      </ErrorBoundary>
      <Row className="isBound">
        <Col xs="12" className={cx('layout-top__no-bottom-padding')}>
          <div>
            {breadcrumbs && (
              <ErrorBoundary name="breadcrumbs">
                <Breadcrumbs staticCrumbs={crumbs} data={breadcrumbs} />
              </ErrorBoundary>
            )}
          </div>
        </Col>
        <Col xs="12">
          <div>
            <ErrorBoundary name="title">
              <Typography variant="h1" weight="display" height="snug" color="primary" uppercase>
                {data?.layouts?.title}
              </Typography>
            </ErrorBoundary>
          </div>
          <div>
            <GiftCardLayoutMainSection
              Content={Content}
              data={data}
              path={path}
              slug={slug}
              isFlowDisabled={isFlowDisabled}
            />
          </div>
        </Col>
      </Row>
    </Row>
  );
};

GiftCardLayout.propTypes = {
  data: shape({
    layouts: shape({
      title: string
    })
  }),
  path: string.isRequired,
  pageId: string.isRequired,
  loading: bool.isRequired,
  pathWithQueryParams: string.isRequired,
  previewLink: string.isRequired,
  slug: string.isRequired
};

GiftCardLayout.defaultProps = {
  data: shape({
    layouts: shape({
      title: ''
    })
  })
};

GiftCardLayout.displayName = 'GiftCardLayout';

GiftCardLayout.dataModel = giftCardDataModel;
